import { MediaPacksResponseItem } from '@calm/evexia-web-sdk';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getWorkflowPackClass } from '@rhg/utils/workflows';
import { useActiveCircle } from '../use-active-circle';
import { useCircle } from '../use-circle';
import { useWorkflowsForRecipientByType } from '../use-workflows';

const filterAndSortStringArray = (arr: string[]) =>
    arr.filter((v, i, a) => a.indexOf(v) === i).sort();

export const clinicalPacksQueryOptions = {
    queryKey: function ({
        packClasses,
        circleId,
    }: {
        packClasses?: string[];
        circleId: number;
    }) {
        return [
            queryKeys.clinicalPacksByPackClass,
            circleId,
            ...(packClasses ? filterAndSortStringArray(packClasses) : []),
        ];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        packClasses,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        packClasses: string[];
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getClinicalPacksByClass({
            circleAuthorizationToken,
            recipientId: circleId,
            _class: filterAndSortStringArray(packClasses),
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data;
    },
    queryOptions: function ({
        packClasses,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        packClasses: string[];
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                packClasses?.length
            ),
            staleTime: 1000 * 60 * 15, // 15 minutes
        };
    },
};

export function useClinicalPacks(
    circleId: number,
    packClasses: string[] = [],
    isOrdered = false
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = clinicalPacksQueryOptions;
    const rHGApp = getRhgAppHeader();

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ packClasses, circleId }),
        queryFn: async () =>
            queryFn({
                getAccessTokenSilently,
                packClasses,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            }),
        ...queryOptions({
            packClasses,
            isAuthenticated,
            circleId,
            circleAuthorizationToken,
        }),
        enabled: !!(
            isAuthenticated &&
            circleId > 0 &&
            circleAuthorizationToken &&
            packClasses?.length
        ),
    });

    const items = isOrdered
        ? packClasses.reduce<MediaPacksResponseItem[]>(
              (orderedPackItems, packClass) => {
                  const packItem = data?.items?.find(
                      (packItem) => packItem.packClassId === packClass
                  );
                  if (packItem) {
                      return [...orderedPackItems, packItem];
                  } else {
                      return orderedPackItems;
                  }
              },
              []
          )
        : data?.items;

    return {
        clinicalPacks: { ...data, items },
        ...otherProps,
    };
}

export function useActiveCircleClinicalPacks(
    packClasses: string[] = [],
    isOrdered = false
) {
    const { activeCircleId } = useActiveCircle();

    return useClinicalPacks(activeCircleId, packClasses, isOrdered);
}

export function useAllClinicalPacks(circleId: number, isOrdered = false) {
    const { workflows } = useWorkflowsForRecipientByType({
        circleId,
        workflowType: 'clinical_program',
    });

    return useClinicalPacks(
        circleId,
        workflows?.reduce((packClasses: string[], workflow) => {
            const packClass = getWorkflowPackClass(workflow);

            return packClass ? [...packClasses, packClass] : packClasses;
        }, []),
        isOrdered
    );
}

export function useAllActiveCircleClinicalPacks(isOrdered = false) {
    const { activeCircleId } = useActiveCircle();

    return useAllClinicalPacks(activeCircleId, isOrdered);
}
